import React, { useMemo, useState } from 'react'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import { formatNumber } from '../../../utilities/helpers'

import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'

import Confirm from '../Confirm/Confirm'

const normalizeSeatId = (seatId) => String(seatId ?? '').trim()

const uniqueSeatIds = (seatIds) => [
  ...new Set((seatIds || []).map(normalizeSeatId).filter(Boolean)),
]

const getHoldLabel = (holdInfo) => {
  if (!holdInfo || holdInfo === true) return null
  if (holdInfo.categoryName && holdInfo.holdName) {
    return `${holdInfo.holdName} (${holdInfo.categoryName})`
  }
  return holdInfo.holdName || holdInfo.categoryName || null
}

const formatHoldNames = (holdLabels) => {
  const uniqueLabels = [...new Set(holdLabels.filter(Boolean))]
  if (uniqueLabels.length === 0) return null
  if (uniqueLabels.length === 1) return uniqueLabels[0]
  if (uniqueLabels.length === 2)
    return `${uniqueLabels[0]} and ${uniqueLabels[1]}`
  return `${uniqueLabels.slice(0, -1).join(', ')}, and ${uniqueLabels.at(-1)}`
}

// shared component in package seatmap and assigning tickets
//
// `mode` decides what the number in the box means:
//   'assign'    - how many seats to pick for this action, replacing any
//                 previous pick for the section (group tickets, exchanges,
//                 assigning a package to a fan).
//   'inventory' - the total number of tickets the section contributes to a
//                 package. Reducing it deletes unsold tickets, so the total is
//                 pre-filled, floored at what fans already hold, and any
//                 reduction has to be confirmed.
export default function SelectGASeats({
  setSelectedSeatIds,
  section,
  seatsToAssignFrom,
  holdMap,
  onClose,
  mode = 'assign',
  currentSeatIds,
  committedSeatIds,
  // 'all' closes every stacked modal (package seatmap page).
  // 'top' only closes this picker so nested hosts like Relocate Seats stay open.
  dismissMode = 'all',
}) {
  // Provider
  const { closeModal, openModal, closeAllModals } = useModal()
  const [validationMessage, setValidationMessage] = useState(null)

  const isInventoryMode = mode === 'inventory'
  const dismissAfterCommit = () => {
    if (dismissMode === 'top') closeModal()
    else closeAllModals()
  }

  const assignableSeatIds = useMemo(
    () => uniqueSeatIds(seatsToAssignFrom),
    [seatsToAssignFrom]
  )

  // Sold and reserved tickets stay in the section no matter what is typed.
  const keptSeatIds = useMemo(
    () => (isInventoryMode ? uniqueSeatIds(committedSeatIds) : []),
    [committedSeatIds, isInventoryMode]
  )

  const { releasableSeatIds, addableSeatIds } = useMemo(() => {
    if (!isInventoryMode) {
      return { releasableSeatIds: [], addableSeatIds: [] }
    }

    const kept = new Set(keptSeatIds)
    const selected = uniqueSeatIds(currentSeatIds)
    const alreadyInSection = new Set([...selected, ...kept])

    return {
      releasableSeatIds: selected.filter((seatId) => !kept.has(seatId)),
      addableSeatIds: assignableSeatIds.filter(
        (seatId) => !alreadyInSection.has(seatId)
      ),
    }
  }, [assignableSeatIds, currentSeatIds, isInventoryMode, keptSeatIds])

  const currentTotal = keptSeatIds.length + releasableSeatIds.length
  const minTotal = keptSeatIds.length
  const maxTotal = isInventoryMode
    ? currentTotal + addableSeatIds.length
    : (seatsToAssignFrom || []).length

  const [value, setValue] = useState(
    isInventoryMode ? String(currentTotal) : ''
  )

  const hasInventory = maxTotal > 0

  const handleClose = () => {
    if (onClose) onClose()
    closeModal()
  }

  const selectGASeats = (sectionId, seats) => {
    setSelectedSeatIds((prevSelected) => {
      const newSelected = { ...prevSelected }
      if (!newSelected.gaSeats) {
        newSelected.gaSeats = {}
      }

      newSelected.gaSeats[sectionId] = seats

      return newSelected
    })
  }

  const commitSeatIds = (seatIds) => {
    selectGASeats(section?.sectionId, seatIds)
    dismissAfterCommit()
    if (onClose) onClose()
  }

  const confirmHeldSeats = (seatIdsBeingAdded, proceed) => {
    const heldLabels = seatIdsBeingAdded
      .map((seatId) => getHoldLabel(holdMap?.get(seatId)))
      .filter(Boolean)
    const hasHeldSeats = seatIdsBeingAdded.some((seatId) =>
      Boolean(holdMap?.get(seatId))
    )

    if (!hasHeldSeats) {
      proceed()
      return
    }

    const holdNames = formatHoldNames(heldLabels)
    const description = holdNames
      ? `Some of these tickets are on hold for "${holdNames}". Would you like to continue and select them for assignment?`
      : 'Some of these tickets are currently on hold. Would you like to continue and select them for assignment?'

    openModal(
      Confirm,
      {
        title: 'Seats on hold',
        description,
        confirmLabel: 'Yes',
        onConfirm: proceed,
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: false }
    )
  }

  // 'assign' mode: the pick replaces whatever was chosen before.
  const selectXSpots = (numberSelected) => {
    const sliceOfSpots = (seatsToAssignFrom || []).slice(0, numberSelected)
    confirmHeldSeats(sliceOfSpots, () => commitSeatIds(sliceOfSpots))
  }

  // 'inventory' mode: kept tickets first, then whatever is already selected,
  // then unused section spots, so growing never disturbs existing tickets and
  // shrinking only gives back unsold ones.
  const buildInventorySelection = (total) => {
    const selection = [...keptSeatIds]

    for (const seatId of [...releasableSeatIds, ...addableSeatIds]) {
      if (selection.length >= total) break
      selection.push(seatId)
    }

    return selection
  }

  const applyInventoryTotal = (total) => {
    const selection = buildInventorySelection(total)

    if (total > currentTotal) {
      const added = addableSeatIds.slice(0, total - currentTotal)
      confirmHeldSeats(added, () => commitSeatIds(selection))
      return
    }

    const removedCount = currentTotal - total
    openModal(
      Confirm,
      {
        title:
          removedCount === 1
            ? 'Remove 1 ticket from this package?'
            : `Remove ${formatNumber(removedCount)} tickets from this package?`,
        description: `This permanently removes ${formatNumber(
          removedCount
        )} unsold ${removedCount === 1 ? 'ticket' : 'tickets'} from this section when you save. ${
          minTotal > 0
            ? `The ${formatNumber(minTotal)} sold or reserved ${
                minTotal === 1 ? 'ticket' : 'tickets'
              } will be kept.`
            : ''
        }`,
        confirmLabel: 'Remove tickets',
        onConfirm: () => commitSeatIds(selection),
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: false }
    )
  }

  const validate = (total) => {
    if (!Number.isInteger(total)) return null

    if (total > maxTotal) {
      return isInventoryMode
        ? `This section can only contribute ${formatNumber(maxTotal)} tickets to the package.`
        : "Number of tickets you've added exceeds the max limit of available tickets."
    }

    if (isInventoryMode && total < minTotal) {
      return `${formatNumber(minTotal)} of these tickets are already sold or reserved, so the total can't go below ${formatNumber(minTotal)}.`
    }

    return null
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!hasInventory) {
      handleClose()
      return
    }

    const numberOfSpots = parseInt(value)
    const message = validate(numberOfSpots)

    if (message) {
      setValidationMessage(message)
      return
    }

    setValidationMessage(null)

    if (isInventoryMode) {
      if (!Number.isInteger(numberOfSpots) || numberOfSpots === currentTotal) {
        handleClose()
        return
      }

      applyInventoryTotal(numberOfSpots)
      return
    }

    if (numberOfSpots > 0) {
      selectXSpots(numberOfSpots)
    }
  }

  const handleInputChange = (val) => {
    setValue(val)
    setValidationMessage(validate(parseInt(val)))
  }

  const isSubmitDisabled = isInventoryMode
    ? value === '' || Boolean(validationMessage)
    : !value || value === '0' || Boolean(validationMessage)

  return (
    <Modal.Body>
      <h4 className="mb-3 fw-bold">
        {isInventoryMode
          ? 'Tickets in this section'
          : 'Add tickets to selection'}
      </h4>

      {isInventoryMode ? (
        <div className="mb-4 text-dark">
          <p className="mb-1">
            Currently in this package: {formatNumber(currentTotal)}
          </p>
          {minTotal > 0 && (
            <p className="mb-1">
              Sold or reserved (always kept): {formatNumber(minTotal)}
            </p>
          )}
          <p className="mb-0">
            Most this section can hold: {formatNumber(maxTotal)}
          </p>
        </div>
      ) : (
        <p className="mb-4 text-dark">
          Total available tickets in this section: {formatNumber(maxTotal)}
        </p>
      )}

      {!hasInventory ? (
        <>
          <Alert
            variant="warning"
            className="d-flex gap-2 mb-4 align-items-center"
          >
            <div className="body">
              <p className="mb-0">
                No inventory is available in this section. All package tickets
                are sold or reserved.
              </p>
            </div>
          </Alert>
          <div className="d-flex justify-content-end">
            <Button
              variant="light"
              type="button"
              className="fw-medium"
              onClick={handleClose}
            >
              Close
            </Button>
          </div>
        </>
      ) : (
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4" controlId="ga-seat-quantity">
            <Form.Label>
              {isInventoryMode
                ? 'Total tickets in this section'
                : 'Number of tickets'}
            </Form.Label>
            <Form.Control
              type="text"
              pattern="^[0-9]*$"
              placeholder={
                isInventoryMode
                  ? 'Enter the total number of tickets'
                  : 'Enter number of tickets'
              }
              max={maxTotal}
              value={value || ''}
              onChange={(e) =>
                e.target.validity.valid || e.target.value === ''
                  ? handleInputChange(e.target.value)
                  : value
              }
              autoFocus
            />
          </Form.Group>

          {validationMessage && (
            <Alert
              variant="danger"
              className="d-flex gap-2 mt-3 mb-4 align-items-center"
            >
              <div className="body">
                <p className="mb-0">{validationMessage}</p>
              </div>
            </Alert>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="light"
              type="button"
              className="fw-medium"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="fw-medium px-4"
              disabled={isSubmitDisabled}
            >
              {isInventoryMode ? 'Update tickets' : 'Add tickets'}
            </Button>
          </div>
        </Form>
      )}
    </Modal.Body>
  )
}
